// apps/provider/src/contexts/CurrentPatientContext.jsx

import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiService } from '@/services/api';

const CurrentPatientContext = createContext();

export function CurrentPatientProvider({ children }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [currentPatient, setCurrentPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Cache to avoid refetching the same patient
  const patientCache = useRef(new Map());
  const fetchPromises = useRef(new Map()); // Prevent duplicate fetches

  // Extract patient ID from various sources
  const extractPatientInfo = () => {
    const pathname = location.pathname;
    
    // 1. Check URL search params first (highest priority)
    const patientIdParam = searchParams.get('patient_id');
    if (patientIdParam) {
      return { patientId: patientIdParam, source: 'param' };
    }
    
    // 2. Check if we're on a patient detail page
    if (pathname.includes('/patients/') && !pathname.includes('/new')) {
      const matches = pathname.match(/\/patients\/([a-f0-9-]+)/);
      if (matches) {
        return { patientId: matches[1], source: 'direct' };
      }
    }
    
    // 3. Check if we're on an encounter page
    if (pathname.includes('/encounters/')) {
      const matches = pathname.match(/\/encounters\/([a-f0-9-]+)/);
      if (matches) {
        return { encounterId: matches[1], source: 'encounter' };
      }
    }
    
    // 4. Check if we're on a clinical note page
    if (pathname.includes('/clinical-notes/')) {
      const matches = pathname.match(/\/clinical-notes\/([a-f0-9-]+)/);
      if (matches) {
        return { noteId: matches[1], source: 'note' };
      }
    }
    
    // 5. Check if we're on prescription page
    if (pathname.includes('/prescriptions/')) {
      const matches = pathname.match(/\/prescriptions\/([a-f0-9-]+)/);
      if (matches) {
        return { prescriptionId: matches[1], source: 'prescription' };
      }
    }
    
    return null;
  };

  // Fetch patient by ID with caching
  const fetchPatient = async (patientId, isRetry = false) => {
    // Check cache first
    if (patientCache.current.has(patientId) && !isRetry) {
      return patientCache.current.get(patientId);
    }
    
    // Check if already fetching
    if (fetchPromises.current.has(patientId) && !isRetry) {
      return fetchPromises.current.get(patientId);
    }
    
    // Create fetch promise
    const fetchPromise = apiService.getPatient(patientId)
      .then(patient => {
        patientCache.current.set(patientId, patient);
        fetchPromises.current.delete(patientId);
        return patient;
      })
      .catch(error => {
        fetchPromises.current.delete(patientId);
        throw error;
      });
    
    fetchPromises.current.set(patientId, fetchPromise);
    return fetchPromise;
  };

  // Fresh fetch that still joins an in-flight request: the patient chart uses
  // this so a cold load issues ONE getPatient shared with the context instead
  // of two, while a revisit (cache warm, nothing in flight) still refreshes.
  const fetchPatientFresh = (patientId) => {
    if (fetchPromises.current.has(patientId)) return fetchPromises.current.get(patientId);
    return fetchPatient(patientId, true);
  };

  // Main effect to handle patient context
  useEffect(() => {
    const updatePatientContext = async () => {
      const info = extractPatientInfo();
      
      // No patient context needed
      if (!info) {
        setCurrentPatient(null);
        setError(null);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        let patient = null;
        
        if (info.patientId) {
          // Direct patient ID
          patient = await fetchPatient(info.patientId);
        } 
        else if (info.encounterId) {
          // Fetch encounter first
          const encounter = await apiService.getEncounter(info.encounterId);
          if (encounter.patient_id) {
            patient = await fetchPatient(encounter.patient_id);
          }
        }
        else if (info.noteId) {
          // Fetch clinical note first
          const note = await apiService.getClinicalNote(info.noteId);
          if (note.patient_id) {
            patient = await fetchPatient(note.patient_id);
          }
        }
        else if (info.prescriptionId) {
          // Fetch prescription first
          const prescription = await apiService.getPrescriptionDetail(info.prescriptionId);
          if (prescription.patient_id) {
            patient = await fetchPatient(prescription.patient_id);
          }
        }
        
        setCurrentPatient(patient);
      } catch (err) {
        console.error('Error fetching patient context:', err);
        
        // Retry once for non-404 errors
        if (!err.message?.includes('404') && info.patientId) {
          try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const patient = await fetchPatient(info.patientId, true);
            setCurrentPatient(patient);
            return;
          } catch (retryErr) {
            console.error('Retry failed:', retryErr);
          }
        }
        
        // Silently fail
        setCurrentPatient(null);
        setError(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    updatePatientContext();
  }, [location.pathname, searchParams]);

  // Clear cache on significant navigation
  useEffect(() => {
    // Clear old cache entries if too many (keep last 10)
    if (patientCache.current.size > 10) {
      const entries = Array.from(patientCache.current.entries());
      const toKeep = entries.slice(-10);
      patientCache.current = new Map(toKeep);
    }
  }, [location.pathname]);

  const value = {
    fetchPatientFresh,
    currentPatient,
    isLoading,
    error,
    refreshPatient: async () => {
      if (currentPatient?.id) {
        // Force refresh by clearing cache
        patientCache.current.delete(currentPatient.id);
        const patient = await fetchPatient(currentPatient.id);
        setCurrentPatient(patient);
        return patient;
      }
    },
    clearPatient: () => {
      setCurrentPatient(null);
      setError(null);
    }
  };

  return (
    <CurrentPatientContext.Provider value={value}>
      {children}
    </CurrentPatientContext.Provider>
  );
}

export function useCurrentPatient() {
  const context = useContext(CurrentPatientContext);
  if (context === undefined) {
    throw new Error('useCurrentPatient must be used within CurrentPatientProvider');
  }
  return context;
}